// File: client/src/components/rules/TestWorkflowModal.js
import React, { useState, useEffect } from 'react';
import {
    Modal,
    Button,
    Tabs,
    Radio,
    Input,
    Space,
    Typography,
    Divider,
    Spin,
    Result,
    Card,
    Timeline,
    message
} from 'antd';
import {
    PlayCircleOutlined,
    LoadingOutlined,
    CheckCircleOutlined,
    CloseCircleOutlined,
    SearchOutlined,
    CodeOutlined,
    FileTextOutlined
} from '@ant-design/icons';
import { useAuth0 } from '@auth0/auth0-react';
import { useSharedData } from '../contexts/SharedDataContext';
import axios from 'axios';
import MonacoEditor from 'react-monaco-editor';

const { TabPane } = Tabs;
const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;

// Editor options
const editorOptions = {
    minimap: { enabled: false },
    scrollBeyondLastLine: false,
    automaticLayout: true,
    fontSize: 14,
    lineNumbers: 'on',
    wordWrap: 'on',
    readOnly: false
};

const TestWorkflowModal = ({ visible, workflow, onClose }) => {
    const [activeTab, setActiveTab] = useState('search');
    const [testMode, setTestMode] = useState('existing');
    const [callId, setCallId] = useState('');
    const [jsonInput, setJsonInput] = useState('{\n  "id": "test-call-123",\n  "qualScore": 85,\n  "callType": "inbound",\n  "agentName": "John Doe"\n}');
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [selectedCall, setSelectedCall] = useState(null);
    const [loading, setLoading] = useState(false);
    const [testing, setTesting] = useState(false);
    const [testResults, setTestResults] = useState(null);
    const [error, setError] = useState(null);

    const { getAccessTokenSilently } = useAuth0();
    const { selectedTenant } = useSharedData();

    // Reset state when modal becomes visible
    useEffect(() => {
        if (visible) {
            setActiveTab('search');
            setTestMode('existing');
            setCallId('');
            setJsonInput('{\n  "id": "test-call-123",\n  "qualScore": 85,\n  "callType": "inbound",\n  "agentName": "John Doe"\n}');
            setSearchQuery('');
            setSearchResults([]);
            setSelectedCall(null);
            setTestResults(null);
            setError(null);
        }
    }, [visible]);

    // Search for calls
    const handleSearch = async () => {
        if (!searchQuery.trim()) {
            message.error('Please enter a search query');
            return;
        }

        setLoading(true);
        try {
            const token = await getAccessTokenSilently();
            const response = await axios.get('/api/calls/search', {
                params: {
                    query: searchQuery,
                    limit: 10
                },
                headers: {
                    Authorization: `Bearer ${token}`,
                    'x-tenant': selectedTenant
                }
            });

            setSearchResults(response.data);

            if (response.data.length === 0) {
                message.info('No calls found matching your search');
            }
        } catch (error) {
            console.error('Error searching calls:', error);
            message.error('Failed to search calls');
        } finally {
            setLoading(false);
        }
    };

    // Select a call from search results
    const handleSelectCall = (call) => {
        setSelectedCall(call);
        setCallId(call.id);
    };

    // Get call by ID
    const handleGetCall = async () => {
        if (!callId.trim()) {
            message.error('Please enter a call ID');
            return;
        }

        setLoading(true);
        try {
            const token = await getAccessTokenSilently();
            const response = await axios.get(`/api/calls/${callId}`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'x-tenant': selectedTenant
                }
            });

            setSelectedCall(response.data);
        } catch (error) {
            console.error('Error getting call:', error);
            message.error('Failed to retrieve call');
            setSelectedCall(null);
        } finally {
            setLoading(false);
        }
    };

    // Parse JSON input
    const handleParseJson = () => {
        try {
            const parsedJson = JSON.parse(jsonInput);
            setSelectedCall(parsedJson);
            message.success('JSON parsed successfully');
        } catch (error) {
            console.error('Error parsing JSON:', error);
            message.error('Invalid JSON format');
            setSelectedCall(null);
        }
    };

    // Execute workflow test
    const handleTestWorkflow = async () => {
        if (!workflow) {
            message.error('No workflow selected');
            return;
        }

        if (!selectedCall) {
            message.error('Please select or provide a call record');
            return;
        }

        setTesting(true);
        setTestResults(null);
        setError(null);

        try {
            const token = await getAccessTokenSilently();
            const response = await axios.post('/api/workflows/test', {
                workflow,
                call: selectedCall
            }, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'x-tenant': selectedTenant
                }
            });

            setTestResults(response.data);
        } catch (error) {
            console.error('Error testing workflow:', error);
            setError(error.response?.data?.message || 'Failed to test workflow');
        } finally {
            setTesting(false);
        }
    };

    // Render search tab
    const renderSearchTab = () => {
        return (
            <div>
                <Space direction="vertical" style={{ width: '100%' }} size="large">
                    <Input.Search
                        placeholder="Search calls by id, agent name, or tags"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        onSearch={handleSearch}
                        enterButton
                        loading={loading}
                    />

                    {loading ? (
                        <div style={{ textAlign: 'center', padding: '20px 0' }}>
                            <Spin indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />} />
                            <div style={{ marginTop: 8 }}>Searching...</div>
                        </div>
                    ) : (
                        searchResults.length > 0 && (
                            <div>
                                <Text>Select a call to use for testing:</Text>
                                <div style={{ marginTop: 12, maxHeight: '300px', overflowY: 'auto' }}>
                                    {searchResults.map(call => (
                                        <Card
                                            key={call.id}
                                            hoverable
                                            style={{
                                                marginBottom: 8,
                                                cursor: 'pointer',
                                                border: selectedCall?.id === call.id ? '2px solid #1890ff' : '1px solid #d9d9d9'
                                            }}
                                            onClick={() => handleSelectCall(call)}
                                        >
                                            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                                <div>
                                                    <Text strong>{call.id}</Text>
                                                    <br />
                                                    <Text type="secondary">Agent: {call.agentName}</Text>
                                                </div>
                                                <div>
                                                    <Text>Quality Score: {call.qualScore}</Text>
                                                    <br />
                                                    <Text type="secondary">Call Type: {call.callType}</Text>
                                                </div>
                                            </div>
                                        </Card>
                                    ))}
                                </div>
                            </div>
                        )
                    )}

                    {selectedCall && activeTab === 'search' && (
                        <div>
                            <Divider>Selected Call</Divider>
                            <Card>
                <pre style={{ maxHeight: '200px', overflow: 'auto' }}>
                  {JSON.stringify(selectedCall, null, 2)}
                </pre>
                            </Card>
                        </div>
                    )}
                </Space>
            </div>
        );
    };

    // Render ID tab
    const renderIdTab = () => {
        return (
            <div>
                <Space direction="vertical" style={{ width: '100%' }} size="large">
                    <Input.Search
                        placeholder="Enter call ID"
                        value={callId}
                        onChange={(e) => setCallId(e.target.value)}
                        onSearch={handleGetCall}
                        enterButton
                        loading={loading}
                    />

                    {loading ? (
                        <div style={{ textAlign: 'center', padding: '20px 0' }}>
                            <Spin indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />} />
                            <div style={{ marginTop: 8 }}>Loading call...</div>
                        </div>
                    ) : selectedCall && activeTab === 'id' ? (
                        <div>
                            <Card>
                <pre style={{ maxHeight: '300px', overflow: 'auto' }}>
                  {JSON.stringify(selectedCall, null, 2)}
                </pre>
                            </Card>
                        </div>
                    ) : null}
                </Space>
            </div>
        );
    };

    // Render JSON tab
    const renderJsonTab = () => {
        return (
            <div>
                <Space direction="vertical" style={{ width: '100%' }} size="large">
                    <div>
                        <Text>Enter JSON representation of a call:</Text>
                        <div style={{ marginTop: 8 }}>
                            <MonacoEditor
                                height="250"
                                language="json"
                                theme="vs-light"
                                value={jsonInput}
                                options={editorOptions}
                                onChange={setJsonInput}
                            />
                        </div>
                        <div style={{ marginTop: 8, textAlign: 'right' }}>
                            <Button onClick={handleParseJson} type="primary">
                                Parse JSON
                            </Button>
                        </div>
                    </div>

                    {selectedCall && activeTab === 'json' && (
                        <div>
                            <Divider>Parsed Call</Divider>
                            <Card>
                <pre style={{ maxHeight: '150px', overflow: 'auto' }}>
                  {JSON.stringify(selectedCall, null, 2)}
                </pre>
                            </Card>
                        </div>
                    )}
                </Space>
            </div>
        );
    };

    // Render test results
    const renderTestResults = () => {
        if (!testResults) return null;

        return (
            <div style={{ marginTop: 16 }}>
                <Divider>Test Results</Divider>

                <Result
                    status={testResults.success ? "success" : "error"}
                    title={testResults.success ? "Workflow Completed Successfully" : "Workflow Failed"}
                    subTitle={testResults.success ? "All nodes executed as expected" : testResults.errorMessage}
                />

                <div style={{ marginTop: 16 }}>
                    <Title level={5}>Execution Path:</Title>
                    <Timeline>
                        {testResults.executionPath.map((step, index) => (
                            <Timeline.Item
                                key={index}
                                color={step.status === 'success' ? 'green' : step.status === 'error' ? 'red' : 'blue'}
                                dot={
                                    step.status === 'success' ? <CheckCircleOutlined /> :
                                        step.status === 'error' ? <CloseCircleOutlined /> :
                                            null
                                }
                            >
                                <Text strong>{step.nodeLabel || step.nodeId}</Text>
                                <br />
                                <Text type="secondary">{step.message || 'Node processed'}</Text>

                                {step.output && (
                                    <div style={{ marginTop: 8 }}>
                                        <Text type="secondary">Output:</Text>
                                        <pre style={{ fontSize: '12px', background: '#f5f5f5', padding: 8, borderRadius: 4 }}>
                      {JSON.stringify(step.output, null, 2)}
                    </pre>
                                    </div>
                                )}
                            </Timeline.Item>
                        ))}
                    </Timeline>
                </div>

                {testResults.updatedCall && (
                    <div style={{ marginTop: 16 }}>
                        <Title level={5}>Updated Call:</Title>
                        <Card>
              <pre style={{ maxHeight: '200px', overflow: 'auto' }}>
                {JSON.stringify(testResults.updatedCall, null, 2)}
              </pre>
                        </Card>
                    </div>
                )}
            </div>
        );
    };

    return (
        <Modal
            title="Test Workflow"
            visible={visible}
            onCancel={onClose}
            width={800}
            footer={[
                <Button key="cancel" onClick={onClose}>
                    Close
                </Button>,
                <Button
                    key="test"
                    type="primary"
                    icon={<PlayCircleOutlined />}
                    onClick={handleTestWorkflow}
                    disabled={!selectedCall || testing}
                    loading={testing}
                >
                    Execute Test
                </Button>
            ]}
        >
            <div>
                <Title level={5}>Select Call for Testing</Title>
                <Tabs activeKey={activeTab} onChange={setActiveTab}>
                    <TabPane
                        tab={<span><SearchOutlined />Search</span>}
                        key="search"
                    >
                        {renderSearchTab()}
                    </TabPane>

                    <TabPane
                        tab={<span><FileTextOutlined />Call ID</span>}
                        key="id"
                    >
                        {renderIdTab()}
                    </TabPane>

                    <TabPane
                        tab={<span><CodeOutlined />JSON</span>}
                        key="json"
                    >
                        {renderJsonTab()}
                    </TabPane>
                </Tabs>

                {error && (
                    <div style={{ marginTop: 16 }}>
                        <Result
                            status="error"
                            title="Workflow Test Failed"
                            subTitle={error}
                        />
                    </div>
                )}

                {renderTestResults()}
            </div>
        </Modal>
    );
};

export default TestWorkflowModal;
